<template>
  <div>
    <!-- ── Half-circle probability ── -->
    <div class="rowBoxes" style="display: flex">
      <div class="chartBox probBox halfCircleBox">
        <div class="smallTitle textBox" style="margin: 0; padding: 0">
          What is the probability of an election being triggered?
        </div>
        <svg class="halfCircleSvg" viewBox="0 0 200 120">
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            stroke="#e0e0e0"
            stroke-width="6"
            fill="none"
            stroke-linecap="round"
          />
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            stroke="#d80000"
            stroke-width="6"
            fill="none"
            stroke-linecap="round"
            class="arcFill"
            :stroke-dasharray="arcLength"
            :stroke-dashoffset="arcOffset"
          />
          <text
            x="100"
            y="86"
            text-anchor="middle"
            font-size="30"
            font-weight="700"
            fill="#2c3e50"
          >
            {{ totalPct }}%
          </text>
          <text
            x="100"
            y="104"
            text-anchor="middle"
            font-size="10"
            fill="#888888"
          >
            election probability
          </text>
        </svg>
      </div>

      <!-- ── Party willingness bars ── -->
      <div class="chartBox probBox">
        <div class="smallTitle textBox" style="margin: 0; padding: 0">
          Party willingness to trigger or support an election
        </div>
        <div class="willingnessRow" v-for="w of willingness" :key="w.code">
          <div class="wLogoWrap">
            <img class="wLogo" :src="getLogoSrc(w.code)" alt="" />
          </div>
          <div class="wContent">
            <div class="wHeader">
              <span class="wName">{{ w.name }}</span>
              <span class="wPct" :style="{ color: w.color }"
                >{{ w.willing }}%</span
              >
            </div>
            <div class="wBarBg">
              <div
                class="wBar"
                :style="{ width: w.willing + '%', background: w.color }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Derived constants ── -->
    <!-- <div class="smallTitle" style="width: 100%">
      {{ $t("ElectionProb.title") || "Election probability model" }}
    </div>
    <div class="constantsGrid">
      <div class="constCard" v-for="c of constants" :key="c.label">
        <div class="constLabel">{{ c.label }}</div>
        <div class="constNum">{{ c.value }}</div>
        <div class="constSrc">{{ c.source }}</div>
      </div>
    </div> -->

    <!-- ── Formula ── -->
    <!-- <div class="formulaBox">
      <span class="fLabel">P<sub>election</sub></span> = max(P<sub>gov</sub>,
      P<sub>opp</sub>)<br />
      <span class="fLabel">P<sub>gov</sub></span> = 95 / (1 + e<sup
        >-1.2 × ((poll − last − Z + urgency) − 1.0)</sup
      >)<br />
      <span class="fLabel">P<sub>opp</sub></span> = P<sub>CPC</sub> ×
      max(P<sub>BQ</sub>, P<sub>NDP</sub>) × seats_factor
    </div> -->

    <!-- ── Polling sliders ── -->
    <div class="chartBox probBox">
      <div
        class="smallTitle textBox"
        style="margin: 0; padding: 0; margin-bottom: 10px"
      >
        Drag to explore scenarios
      </div>
      <div class="sliderGroup" v-for="p of partySliders" :key="p.code">
        <div class="sliderHeader">
          <div class="pName">
            <span class="dot" :style="{ background: p.color }"></span>
            {{ p.name }}
          </div>
          <div class="readout">{{ p.poll.toFixed(1) }}%</div>
        </div>
        <div class="sliderMeta">
          <span>2025 result: {{ lastElection[p.code].toFixed(1) }}%</span>
          <span :class="diffClass(p.poll - lastElection[p.code])">
            {{ diffStr(p.poll - lastElection[p.code]) }} vs. 2025
          </span>
        </div>
        <div class="sliderWrap">
          <input
            type="range"
            :min="p.min"
            :max="p.max"
            :step="p.step"
            v-model.number="p.poll"
            :style="{
              '--fill': fillPct(p.poll, p.min, p.max) + '%',
              '--color': p.color,
            }"
          />
          <div
            class="marker"
            :style="{
              background: p.color,
              left: markerLeft(p),
            }"
          ></div>
          <div
            class="markerLabel"
            :style="{ color: p.color, left: markerLeft(p) }"
          >
            current polling
          </div>
        </div>
      </div>
    </div>

    <!-- ── Urgency ── -->
    <!-- <div class="slidersCard">
      <div class="sliderHeader">
        <div class="pName" style="font-weight: bold">
          Urgency factor (manual)
        </div>
        <div class="readout">
          {{ urgency >= 0 ? "+" : "" }}{{ urgency.toFixed(1) }}
        </div>
      </div>
      <input
        type="range"
        min="-15"
        max="15"
        step="0.5"
        v-model.number="urgency"
        :style="{
          width: '100%',
          'accent-color': '#d80000',
          '--fill': fillPct(urgency, -15, 15) + '%',
        }"
      />
      <div class="refTags">
        <span class="refTag">Trump effect: {{ urgencyEffects.trump }}</span>
        <span class="refTag"
          >Liberal weariness: {{ urgencyEffects.libweariness }}</span
        >
        <span class="refTag"
          >New leader bounce: {{ urgencyEffects.newleader }}</span
        >
        <span class="refTag"
          >Opposition fragmentation: {{ urgencyEffects.opp }}</span
        >
      </div>
    </div> -->

    <!-- ── Fixed inputs ── -->
    <!-- <div class="sectionLabel">
      Fixed inputs — 2025 election results + seat math
    </div>
    <div class="fixedCard">
      <div class="fixedRow" v-for="f of fixedInputs" :key="f.label">
        <span class="fl">{{ f.label }}</span>
        <span class="fv">{{ f.value }}</span>
      </div>
    </div> -->

    <div class="additional" style="text-align: left">
      <span style="font-weight: bold">Methodology: </span>
      Constants derived from all Canadian federal confidence votes 2019–2025.
      Polling averages sourced from aggregated public polls. Sigmoid curves
      calibrated to observed party behaviour thresholds.
    </div>
  </div>
</template>

<script>
import axios from "axios";

/* ── Derived constants from 2019–2025 data ── */
const Z = 3.0;
const LAST_ELECTION = { LPC: 43.1, CPC: 41.7, NDP: 6.1, BQ: 6.7 };
const GOV_SEATS = 174;
const OPP_SEATS = 169;
const THRESHOLDS = { CPC: -3.0, BQ: 0.0, NDP: 5.0 };

/* ── Fallback current polling (Mar 2026 averages) ── */
const FALLBACK_POLLING = { LPC: 46.0, CPC: 35.0, NDP: 7.0, BQ: 6.0 };

function sigmoid(x, k, x0) {
  return 95 / (1 + Math.exp(-k * (x - x0)));
}

export default {
  name: "ElectionProbability",
  props: {
    currentView: String,
  },
  data() {
    return {
      lastElection: { ...LAST_ELECTION },
      urgencyEffects: {
        trump: +5.0,
        libweariness: -5.0,
        newleader: +2.0,
        opp: +1.0,
      },
      urgency: 3.0,
      partySliders: [
        {
          code: "LPC",
          name: "Liberal",
          color: "#D71921",
          min: 15,
          max: 55,
          step: 0.5,
          poll: FALLBACK_POLLING.LPC,
          currentPoll: FALLBACK_POLLING.LPC,
        },
        {
          code: "CPC",
          name: "Conservative",
          color: "#0F2D52",
          min: 15,
          max: 55,
          step: 0.5,
          poll: FALLBACK_POLLING.CPC,
          currentPoll: FALLBACK_POLLING.CPC,
        },
        {
          code: "NDP",
          name: "NDP",
          color: "#F58220",
          min: 1,
          max: 30,
          step: 0.5,
          poll: FALLBACK_POLLING.NDP,
          currentPoll: FALLBACK_POLLING.NDP,
        },
        {
          code: "BQ",
          name: "Bloc Québécois",
          color: "#00A7EC",
          min: 1,
          max: 20,
          step: 0.5,
          poll: FALLBACK_POLLING.BQ,
          currentPoll: FALLBACK_POLLING.BQ,
        },
      ],
      constants: [
        {
          label: "Z — trigger penalty",
          value: "3.0 pts",
          source: "2021: LPC polled 35.6%, got 32.6% on E-day",
        },
        {
          label: "X — gov. trigger threshold",
          value: "+2.5 pts",
          source: "Min lead over last election at which a PM triggered",
        },
        {
          label: "Y(CPC)",
          value: "-3.0",
          source: "Always votes for election",
        },
        {
          label: "Y(BQ)",
          value: "0.0",
          source: "Flips at roughly even",
        },
        {
          label: "Y(NDP)",
          value: "+5.0",
          source: "Never voted yes despite +2.2",
        },
        {
          label: "Y(GPC)",
          value: "0.0",
          source: "Policy-driven, similar to BQ",
        },
      ],
      fixedInputs: [
        { label: "Liberal (LPC) — 2025 result", value: "43.1%" },
        { label: "Conservative (CPC) — 2025 result", value: "41.7%" },
        { label: "NDP — 2025 result", value: "6.1%" },
        { label: "Bloc Québécois (BQ) — 2025 result", value: "6.7%" },
        { label: "Government seats (LPC)", value: "169" },
        { label: "Opposition seats (combined)", value: "174" },
      ],
    };
  },
  computed: {
    govNet() {
      const lpc = this.getParty("LPC");
      return lpc.poll - this.lastElection.LPC - Z + this.urgency;
    },
    pGov() {
      return Math.min(95, Math.max(2, sigmoid(this.govNet, 1.2, 1.0)));
    },
    pGovPct() {
      return Math.round(this.pGov);
    },
    cpcDiff() {
      return this.getParty("CPC").poll - this.lastElection.CPC;
    },
    bqDiff() {
      return this.getParty("BQ").poll - this.lastElection.BQ;
    },
    ndpDiff() {
      return this.getParty("NDP").poll - this.lastElection.NDP;
    },
    pCPC() {
      return sigmoid(this.cpcDiff, 1.5, THRESHOLDS.CPC);
    },
    pBQ() {
      return sigmoid(this.bqDiff, 1.5, THRESHOLDS.BQ);
    },
    pNDP() {
      return sigmoid(this.ndpDiff, 1.5, THRESHOLDS.NDP);
    },
    pOpp() {
      const seatsFactor = OPP_SEATS > GOV_SEATS ? 1.0 : 0.15;
      const swingWilling = Math.max(this.pBQ, this.pNDP);
      return Math.min(
        95,
        (this.pCPC / 100) * (swingWilling / 100) * seatsFactor * 100,
      );
    },
    pOppPct() {
      return Math.round(this.pOpp);
    },
    total() {
      return Math.min(
        95,
        Math.max(
          2,
          Math.max(
            this.pGov,
            this.pOpp + (1 - this.pOpp / 100) * this.pGov * 0.1,
          ),
        ),
      );
    },
    totalPct() {
      return Math.round(this.total);
    },
    arcLength() {
      return Math.PI * 80;
    },
    arcOffset() {
      return this.arcLength * (1 - this.totalPct / 100);
    },
    willingness() {
      const lpc = this.getParty("LPC");
      return [
        {
          code: "LPC",
          name: "Liberal (gov)",
          diff: lpc.poll - this.lastElection.LPC,
          net: this.govNet,
          threshold: "X=+2.5, Z=3.0",
          willing: this.pGovPct,
          color: "#D71921",
        },
        {
          code: "CPC",
          name: "Conservative",
          diff: this.cpcDiff,
          threshold: THRESHOLDS.CPC.toFixed(1),
          willing: Math.round(this.pCPC),
          color: "#0F2D52",
        },
        {
          code: "BQ",
          name: "Bloc Québécois",
          diff: this.bqDiff,
          threshold: THRESHOLDS.BQ.toFixed(1),
          willing: Math.round(this.pBQ),
          color: "#00A7EC",
        },
        {
          code: "NDP",
          name: "NDP",
          diff: this.ndpDiff,
          threshold: THRESHOLDS.NDP.toFixed(1),
          willing: Math.round(this.pNDP),
          color: "#F58220",
        },
      ];
    },
  },
  async mounted() {
    await this.fetchCurrentPolling();
  },
  methods: {
    async fetchCurrentPolling() {
      try {
        const response = await axios.get(
          "https://docs.google.com/spreadsheets/d/e/2PACX-1vSHdxj1KPFMHP5gT1ybNfd_-N_7QikiUQ1zMKJgGb4xZjwPY4DcRE6xjWHlWqgbhawjk8ZO6I52MakV/pub?gid=1804583268&single=true&output=csv",
        );
        const parsed = this.parseData(response.data);
        if (parsed[0].length > 0 && parsed[1].length > 0) {
          this.applyPollingData(parsed[0], parsed[1]);
        }
      } catch (err) {
        console.warn(
          "ElectionProbability: Could not fetch live polling, using fallback.",
          err,
        );
      }
    },
    parseData(data) {
      try {
        return data.split(/\r?\n/).map((line) => line.split(",").slice(1));
      } catch (error) {
        console.error("Error parsing data:", error);
        return [[], []];
      }
    },
    applyPollingData(partyNames, partyValues) {
      const nameMap = {
        cpc: "CPC",
        conservative: "CPC",
        lpc: "LPC",
        liberal: "LPC",
        ndp: "NDP",
        bq: "BQ",
        bloc: "BQ",
        "bloc québécois": "BQ",
        green: "GPC",
      };

      for (let i = 0; i < partyNames.length; i++) {
        const raw = (partyNames[i] || "").trim().toLowerCase();
        const code = nameMap[raw];
        if (!code) continue;

        const val = parseFloat(partyValues[i]);
        if (isNaN(val)) continue;

        const slider = this.partySliders.find((s) => s.code === code);
        if (slider) {
          slider.poll = val;
          slider.currentPoll = val;
        }
      }
    },
    getParty(code) {
      return this.partySliders.find((s) => s.code === code);
    },
    markerLeft(p) {
      const frac = (p.currentPoll - p.min) / (p.max - p.min);
      return `calc(${frac} * (100% - 16px) + 8px)`;
    },
    diffStr(diff) {
      return (diff >= 0 ? "+" : "") + diff.toFixed(1);
    },
    diffClass(diff) {
      return diff >= 0 ? "diffPos" : "diffNeg";
    },
    getLogoSrc(code) {
      return require("../../assets/partyLogos/FederalView/" + code + ".png");
    },
    fillPct(val, min, max) {
      return ((val - min) / (max - min)) * 100;
    },
    barColor(pct) {
      if (pct < 25) return "#3D9B35";
      if (pct < 50) return "#BA7517";
      if (pct < 75) return "#D85A30";
      return "#A32D2D";
    },
  },
};
</script>

<style scoped>
/* ── Party willingness bars ── */
.willingnessCard {
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 12px;
}
.willingnessRow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}
.willingnessRow:last-child {
  border-bottom: none;
}
.wLogoWrap {
  flex-shrink: 0;
}
.wLogo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}
.wContent {
  flex: 1;
  min-width: 0;
}
.wHeader {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 4px;
}
.wName {
  font-size: 13px;
  color: #2c3e50;
  font-weight: 500;
}
.wPct {
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.wBarBg {
  background: #e0e0e0;
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
}
.wBar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}
.probBox {
  padding: 20px;
}
.halfCircleBox {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 50%;
}
.halfCircleSvg {
  max-width: 300px;
  width: 100%;
}
.arcFill {
  transition: stroke-dashoffset 0.6s ease;
}
/* ── Gauge charts ── */
.gaugesRow {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.gaugeWrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 120px;
}
.gaugeChart {
  width: 100%;
}
.gaugeLabel {
  font-size: 12px;
  color: #888888;
  text-align: center;
  margin-top: 4px;
  font-weight: 500;
}
/* ── Result box ── */
.resultBox {
  background: #ffffff;
  border: 2px solid #cccccc;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  margin-bottom: 20px;
}
.resultPct {
  font-size: 56px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #2c3e50;
  line-height: 1;
}
.resultLabel {
  font-size: 15px;
  color: #666666;
  margin-top: 8px;
  margin-bottom: 16px;
}
.breakdownRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
}
.breakdownItem {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 12px 16px;
  text-align: left;
}
.biLabel {
  font-size: 12px;
  color: #888888;
}
.biVal {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  font-variant-numeric: tabular-nums;
  margin-bottom: 8px;
}
.probBarWrap {
  background: #e0e0e0;
  border-radius: 6px;
  height: 28px;
  position: relative;
  overflow: hidden;
}
.probBar {
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s ease, background 0.3s ease;
  display: flex;
  align-items: center;
  padding-left: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  min-width: 40px;
}

/* ── Section titles ── */
/* .smallTitle {
  color: #2c3e50;
  font-size: 20px;
  font-weight: bold;
  font-family: Merriweather, serif;
  margin-top: 20px;
  margin-bottom: 16px;
} */
.sectionLabel {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #888888;
  margin-bottom: 10px;
  margin-top: 20px;
  padding-bottom: 6px;
  border-bottom: 1px solid #dddddd;
}

/* ── Constants grid ── */
.constantsGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
.constCard {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 12px;
}
.constLabel {
  font-size: 12px;
  color: #888888;
  margin-bottom: 4px;
}
.constNum {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  font-variant-numeric: tabular-nums;
}
.constSrc {
  font-size: 11px;
  color: #aaaaaa;
  margin-top: 6px;
  line-height: 1.4;
}

/* ── Formula ── */
.formulaBox {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px 20px;
  font-family: monospace;
  font-size: 13px;
  line-height: 1.9;
  color: #2c3e50;
  margin-bottom: 16px;
  overflow-x: auto;
}
.fLabel {
  color: #888888;
}

/* ── Fixed inputs ── */
.fixedCard {
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 10px;
  padding: 12px 20px;
  margin-bottom: 16px;
}
.fixedRow {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid #eeeeee;
}
.fixedRow:last-child {
  border-bottom: none;
}
.fl {
  color: #888888;
}
.fv {
  font-weight: 600;
  color: #2c3e50;
  font-variant-numeric: tabular-nums;
}

/* ── Sliders card ── */
.slidersCard {
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 12px;
}
.sliderGroup {
  margin-bottom: 18px;
}
.sliderGroup:last-child {
  margin-bottom: 0;
}
.sliderHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}
.pName {
  font-size: 13px;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.readout {
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #2c3e50;
}
.sliderMeta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #aaaaaa;
  margin-bottom: 4px;
}
.sliderWrap {
  position: relative;
  height: 32px;
  display: flex;
  align-items: center;
}
.sliderWrap input[type="range"] {
  width: 100%;
  position: relative;
  z-index: 2;
  accent-color: #d80000;
  cursor: pointer;
}
input[type="range"] {
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
}
input[type="range"]::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(
    to right,
    var(--color, #888) var(--fill, 50%),
    #e0e0e0 var(--fill, 50%)
  );
}
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color, #888);
  margin-top: -6px;
  transition: box-shadow 0.15s ease;
}
input[type="range"]:hover::-webkit-slider-thumb {
  box-shadow: 0 0 0 6px color-mix(in srgb, var(--color, #888) 20%, transparent);
}
input[type="range"]::-moz-range-track {
  height: 4px;
  border-radius: 2px;
  background: #e0e0e0;
}
input[type="range"]::-moz-range-progress {
  height: 4px;
  border-radius: 2px;
  background: var(--color, #888);
}
input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color, #888);
  border: none;
  transition: box-shadow 0.15s ease;
}
input[type="range"]:hover::-moz-range-thumb {
  box-shadow: 0 0 0 6px color-mix(in srgb, var(--color, #888) 20%, transparent);
}
.marker {
  position: absolute;
  z-index: 3;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 2px;
  height: 28px;
  border-radius: 1px;
  pointer-events: none;
}
.markerLabel {
  position: absolute;
  z-index: 3;
  top: -14px;
  font-size: 10px;
  font-weight: 600;
  transform: translateX(-50%);
  white-space: nowrap;
  pointer-events: none;
}
.diffPos {
  color: #3d9b35;
}
.diffNeg {
  color: #c0392b;
}

/* ── Urgency reference tags ── */
.refTags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.refTag {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 6px;
  background: #f0f0f0;
  color: #888888;
}

/* ── Willingness table ── */
.willingnessTable {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.willingnessTable th {
  text-align: left;
  padding: 6px 10px;
  font-weight: 600;
  font-size: 12px;
  color: #888888;
  border-bottom: 1px solid #cccccc;
}
.willingnessTable td {
  padding: 6px 10px;
  border-bottom: 1px solid #eeeeee;
  color: #2c3e50;
  font-variant-numeric: tabular-nums;
}
.netLabel {
  color: #aaaaaa;
  font-size: 12px;
}

/* ── Additional text ── */
.additional {
  margin-top: 16px;
  font-size: 13px;
  color: #666666;
  line-height: 1.6;
}
@media only screen and (max-width: 810px) {
  .rowBoxes {
    flex-direction: column;
  }
  .halfCircleBox {
    width: fit-content;
  }
}
</style>
